"use strict";
/**
 * Copyright © 2019 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.withFacet = exports.asPassThough = exports.perserveMeta = exports.withAppendDesc = exports.withDesc = exports.withAppendDisplayName = exports.withAppendTitle = exports.withTerm = exports.withDisplayName = exports.withTitle = exports.withMeta = void 0;

var react_1 = __importDefault(require("react"));

var lodash_1 = require("lodash");

function customizer(objValue, srcValue) {
  if (lodash_1.isArray(objValue)) {
    return objValue.concat(srcValue);
  }

  return undefined;
}

var asPassThough = function asPassThough(Component) {
  return Component;
};

exports.asPassThough = asPassThough;
/**
 * Creates an HOC use it to attach meta data in an hoc.
 * @param Component The component to wrap.
 */

var withOutMeta = function withOutMeta(Component) {
  return function (props) {
    return react_1.default.createElement(Component, __assign({}, props));
  };
};
/**
 * withMeta creates an HOC that will add meta data to a React Component
 * @param meta the data to be side loaded in to the component
 */


var withMeta = function withMeta(meta) {
  return function (Component) {
    var newMeta = lodash_1.mergeWith({}, Component, meta, customizer);
    return Object.assign(withOutMeta(Component), newMeta);
  };
};

exports.withMeta = withMeta;
/**
 * with Title returns an HOC that sideloads a title to a component
 * @param title The title to be added
 */

var withTitle = function withTitle(title) {
  return function (Component) {
    return withMeta({
      title: title
    })(Component);
  };
};

exports.withTitle = withTitle;
/**
 * withAppendTitle returns an HOC that appends to the sideload title of the component
 * Note it appends to the title with a space.
 * @param title The title to be appended
 */

var withAppendTitle = function withAppendTitle(newTitle) {
  return function (Component) {
    var title = Component.title;

    if (title) {
      return withTitle(title + " " + newTitle)(Component);
    }

    return withTitle(newTitle)(Component);
  };
};

exports.withAppendTitle = withAppendTitle;
/**
 * withDisplayName returns an HOC that sideloads a displayName to a component
 * @param displayName The displayName to be added
 */

var withDisplayName = function withDisplayName(displayName) {
  return function (Component) {
    return withMeta({
      displayName: displayName
    })(Component);
  };
};

exports.withDisplayName = withDisplayName;
/**
 * withAppendDisplayName returns a HOC that appends a name to the sideloaded DisplayName
 * @param newDisplayName the Display name to append
 */

var withAppendDisplayName = function withAppendDisplayName(newDisplayName) {
  return function (Component) {
    var displayName = Component.displayName;

    if (displayName) {
      return withDisplayName(displayName + newDisplayName)(Component);
    }

    return withDisplayName(newDisplayName)(Component);
  };
};

exports.withAppendDisplayName = withAppendDisplayName;
/**
 * withDesc returns an HOC that sideloads the provided discription to the component.
 * @param description the discription to add
 */

var withDesc = function withDesc(description) {
  return function (Component) {
    return withMeta({
      description: description
    })(Component);
  };
};

exports.withDesc = withDesc;
/**
 * withAppendDesc returns an HOC that appends a description to the component sideload description.
 * @param newDescription the description to be appened
 */

var withAppendDesc = function withAppendDesc(newDescription) {
  return function (Component) {
    var description = Component.description ? Component.description + newDescription : newDescription;
    return withDesc(description)(Component);
  };
};

exports.withAppendDesc = withAppendDesc;
/**
 * withTerm returns a function that then takes a term and that returns an HOC that side loads
 * the category and term on to the component.
 * @param cat that category to use in adding a term
 * @param term the term to add
 */

var withTerm = function withTerm(cat) {
  return function (term) {
    return function (Component) {
      var _a;

      return withMeta({
        categories: (_a = {}, _a[cat] = [term], _a)
      })(Component);
    };
  };
};

exports.withTerm = withTerm;
/**
 * preserveMeta returns takes an hoc and returns another one that will apply the hoc but preserve
 * theMeta data from the component.
 * @param hoc the hoc to wrap.
 */

var perserveMeta = function perserveMeta(hoc) {
  return function (Component) {
    return withMeta(Component)(hoc(Component));
  };
};

exports.perserveMeta = perserveMeta;
/**
 * withFacet is expect to be passed to an on function and takes a term and and hoc (using curring)
 *  and returns a Varient that can be used in the on function
 * @param cat Category that the Component will be apart
 * @param term the Term in the Category associated with the Component
 * @param hocs the HOC to apply to the Component
 */

var withFacet = function withFacet(cat) {
  return function (term) {
    return function () {
      var hocs = [];

      for (var _i = 0; _i < arguments.length; _i++) {
        hocs[_i] = arguments[_i];
      }

      return lodash_1.flow(perserveMeta(lodash_1.flow.apply(void 0, hocs)), withTerm(cat)(term), withAppendTitle(term), withAppendDisplayName(term.replace(/ /, '')), withAppendDesc(cat + ": " + term + "\n"));
    };
  };
};

exports.withFacet = withFacet;